import { useFilterOrganizations } from '@/clients/organization/useOrganization.ts';
import { useFulfillmentContext } from '../context/FulfillmentContext.tsx';
import type { ProductFallbackDataMap } from '../types/ProductFallbackDataMap.ts';
import { useMemo } from 'react';
import type { ComboboxOption } from '@schuettflix/react-components';
import { useTranslation } from 'react-i18next';
import { PRODUCT_CATEGORY_LABEL } from '@/phrase/ProductCategoryLabels.ts';

export function useIndividualLineItemProductOptions(
    productFallbackDataMap: ProductFallbackDataMap
): ComboboxOption<string>[] {
    const { t } = useTranslation();
    const { products, productGroup } = useFulfillmentContext();

    const orgIds = useMemo(() => {
        const relevantPartnerIds = [
            ...products.flatMap(product => product.quote?.partnerOrganizationId),
            ...Object.values(productFallbackDataMap).map(p => p.partnerId),
        ];
        const orgIds = [...new Set([...relevantPartnerIds].flatMap(id => (id ? String(id) : [])))];
        return orgIds;
    }, [productFallbackDataMap, products]);

    const { data: relevantPartnerList } = useFilterOrganizations({ ids: orgIds });

    return useMemo(() => {
        return products.flatMap((product, index) =>
            (productGroup.type === 'MERCHANT' || productGroup.type === 'FRANCO') && product.category === 'TRANSPORT'
                ? [] // for merchant and franco, we don't allow the selection of transports
                : {
                      value: product.id,
                      label:
                          `${index + 1}. ` +
                          (product.quote?.name ||
                              productFallbackDataMap[product.id]?.productName ||
                              `[${t(PRODUCT_CATEGORY_LABEL[product.category])}]`),
                      description: relevantPartnerList?.items.find(
                          org =>
                              org.id === product.quote?.partnerOrganizationId ||
                              productFallbackDataMap[product.id]?.partnerId
                      )?.name,
                  }
        );
    }, [productFallbackDataMap, products, productGroup.type, relevantPartnerList?.items, t]);
}
